import { FC, useState, useMemo } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import type { ScoringType, GeographyBand } from "@/lib/scoring/types";
import { useTaxonomyContext } from "@/contexts/Taxonomy/TaxonomyContext";
import { GeoCountryCheckboxList } from "@/lib/company-filter/components/GeoCountryCheckboxList";
import { GeoRegionsSelector } from "@/lib/company-filter/components/GeoRegionsSelector";
import { DEFAULT_REGIONS } from "@/lib/company-filter/utils/geoSelection";
import { CountryInput } from "@/components/filters/Locations/CountryInput";
import { StateInput } from "@/components/filters/Locations/StateInput";
import type { RegionId } from "@/lib/company-filter/types";
import { pluralize } from "@/utils/text";

export const initialBand: GeographyBand = { countryAlpha2s: [], score: 0 };
export const hasErrors = (bands: unknown[]) =>
  (bands as GeographyBand[]).some(
    (b) =>
      b.score <= 0 ||
      (!b.countryAlpha2s?.length &&
        !b.regionNames?.length &&
        !b.stateIds?.length)
  );
export const getBandSummary = (band: unknown) => {
  const b = band as GeographyBand;
  if (b.stateIds?.length)
    return `${b.stateIds.length} ${pluralize("state/province", b.stateIds.length)}`;
  if (b.regionNames?.length) return b.regionNames.join(", ");
  if (b.countryAlpha2s?.length)
    return `${b.countryAlpha2s.length} ${pluralize("country", b.countryAlpha2s.length)}`;
  return "—";
};

type GeoFilterType = "country" | "region" | "state";

function detectFilterType(bands: GeographyBand[]): GeoFilterType {
  if (bands.some((b) => b.stateIds?.length)) return "state";
  if (bands.some((b) => b.regionNames?.length)) return "region";
  return "country";
}

interface Props {
  criterion: ScoringType;
  onChange: (updated: ScoringType) => void;
}

export const GeographyEditor: FC<Props> = ({ criterion, onChange }) => {
  const { countries } = useTaxonomyContext();
  const bands = criterion.geography ?? [];

  const [filterType, setFilterType] = useState<GeoFilterType>(() =>
    detectFilterType(bands)
  );

  // Per-band search state (country checkbox list only)
  const [countrySearch, setCountrySearch] = useState<Record<number, string>>(
    {}
  );

  const countryOptions = useMemo(
    () =>
      countries
        .filter((c) => c.alpha2 && c.name)
        .map((c) => ({ code: c.alpha2!, name: c.name! })),
    [countries]
  );

  // Derive the selected state-mode country from the first band
  const stateCountry = bands[0]?.countryAlpha2s?.[0] ?? "US";

  const emptyBandForType = (type: GeoFilterType): GeographyBand => {
    if (type === "state")
      return { countryAlpha2s: [stateCountry], stateIds: [], score: 0 };
    if (type === "region") return { regionNames: [], score: 0 };
    return { countryAlpha2s: [], score: 0 };
  };

  const handleFilterTypeChange = (
    _: unknown,
    newType: GeoFilterType | null
  ) => {
    if (!newType) return;
    setFilterType(newType);
    onChange({ ...criterion, geography: [emptyBandForType(newType)] });
  };

  const handleStateCountryChange = (alpha2s: string[]) => {
    const alpha2 = alpha2s[0] ?? "US";
    const newBands = bands.map((b) => ({
      countryAlpha2s: [alpha2],
      stateIds: [],
      score: b.score,
    }));
    onChange({ ...criterion, geography: newBands });
  };

  const addBand = () => {
    onChange({
      ...criterion,
      geography: [...bands, emptyBandForType(filterType)],
    });
  };

  const updateBand = (index: number, patch: Partial<GeographyBand>) => {
    onChange({
      ...criterion,
      geography: bands.map((b, i) => (i === index ? { ...b, ...patch } : b)),
    });
  };

  const removeBand = (index: number) => {
    onChange({
      ...criterion,
      geography: bands.filter((_, i) => i !== index),
    });
  };

  return (
    <Box sx={{ px: 2 }}>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={filterType}
        onChange={handleFilterTypeChange}
        sx={{
          mb: 1.5,
          "& .MuiToggleButton-root": { py: 0.5, px: 1.5, fontSize: 12 },
        }}
      >
        <ToggleButton value="region">Geopolitical</ToggleButton>
        <ToggleButton value="country">Country</ToggleButton>
        <ToggleButton value="state">State/Province</ToggleButton>
      </ToggleButtonGroup>

      {/* Country selector for state mode */}
      {filterType === "state" && (
        <Box sx={{ mb: 1.5 }}>
          <CountryInput
            selectedItems={[stateCountry]}
            onCountrySelect={handleStateCountryChange}
            isMultiple={false}
            placeholderText="Select country…"
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, display: "block" }}
          >
            Showing states/provinces for the selected country
          </Typography>
        </Box>
      )}

      {bands.map((band, i) => (
        <Box
          key={i}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 1.5,
            mb: 1,
          }}
        >
          {filterType === "country" && (
            <GeoCountryCheckboxList
              title="Countries"
              countries={countryOptions}
              selectedCodes={band.countryAlpha2s ?? []}
              searchValue={countrySearch[i] ?? ""}
              onSearchChange={(v) =>
                setCountrySearch((prev) => ({ ...prev, [i]: v }))
              }
              onToggle={(code, checked) =>
                updateBand(i, {
                  countryAlpha2s: checked
                    ? [...(band.countryAlpha2s ?? []), code]
                    : (band.countryAlpha2s ?? []).filter((c) => c !== code),
                })
              }
            />
          )}

          {filterType === "region" && (
            <GeoRegionsSelector
              regions={DEFAULT_REGIONS}
              selectedRegionIds={
                (band.regionNames ?? []).filter((r): r is RegionId =>
                  DEFAULT_REGIONS.some((dr) => dr.id === r)
                ) as RegionId[]
              }
              onToggle={(regionId, checked) =>
                updateBand(i, {
                  regionNames: checked
                    ? [...(band.regionNames ?? []), regionId]
                    : (band.regionNames ?? []).filter((r) => r !== regionId),
                })
              }
            />
          )}

          {filterType === "state" && (
            <StateInput
              countryAlpha2={stateCountry}
              selectedItems={band.stateIds ?? []}
              onStateSelect={(_, stateIds) => updateBand(i, { stateIds })}
            />
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1,
              mt: 1,
            }}
          >
            <TextField
              size="small"
              type="number"
              label="Pts"
              error={band.score === 0}
              value={band.score === 0 ? "" : band.score}
              onChange={(e) =>
                updateBand(i, {
                  score: e.target.value === "" ? 0 : Number(e.target.value),
                })
              }
              inputProps={{ min: 0, step: 1 }}
              sx={{ width: 80 }}
            />
            <IconButton
              size="small"
              onClick={() => removeBand(i)}
              aria-label="Remove band"
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      ))}

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={addBand}
        sx={{ color: "primary.main", fontWeight: 600 }}
      >
        ADD BAND
      </Button>
    </Box>
  );
};
